import { c as _c } from "react/compiler-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { COLORS } from '@/styles/tokens/colors';
import { DependencyExclusionReason, FormField, FormFieldDependsOn, FormSection, getDependencyFieldCandidates, getDependencyQuestionIds, getSectionDependencyPreview } from './AdminFormsPage.model';

/** Questions shown in an expanded section row before "Show all". */
const SECTION_PREVIEW_LIMIT = 4;
const SECTION_OPTION_PREFIX = 'section:';
const FIELD_OPTION_PREFIX = 'field:';
const EXCLUSION_REASONS: Record<DependencyExclusionReason, string> = {
  self: 'this question',
  ownChild: 'a follow-up of this question',
  container: 'a list or group answer, which has no citations to pass on',
  hasDependsOn: 'uses answers from other questions. Add it as a question to chain it.'
};
const questionCount = (count: number) => `${count} question${count === 1 ? '' : 's'}`;
interface DependsOnEditorProps {
  field: FormField;
  sections: FormSection[];
  /** Canvas position labels by question ID, such as `1.2`. */
  positions: Map<string, string>;
  onChange: (dependsOn: FormFieldDependsOn | undefined) => void;
}

/**
 * Edits the questions whose answers this question reads as settled findings.
 * Each row is one entry in `dependsOn.sectionIds` or `dependsOn.fieldIds`. A
 * section row expands to show which of its questions the server includes.
 */
export function DependsOnEditor(t0) {
  const $ = _c(88);
  const {
    field,
    sections,
    positions,
    onChange
  } = t0;
  const [isAdding, setIsAdding] = useState(false);
  let t1;
  if ($[0] !== field.dependsOn?.sectionIds) {
    t1 = field.dependsOn?.sectionIds ?? [];
    $[0] = field.dependsOn?.sectionIds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sectionIds = t1;
  let t2;
  if ($[2] !== field.dependsOn?.fieldIds) {
    t2 = field.dependsOn?.fieldIds ?? [];
    $[2] = field.dependsOn?.fieldIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fieldIds = t2;
  const hasDependencies = sectionIds.length + fieldIds.length > 0;
  let T0;
  let T1;
  let fieldsById;
  let options;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let update;
  if ($[4] !== field || $[5] !== fieldIds || $[6] !== hasDependencies || $[7] !== onChange || $[8] !== positions || $[9] !== sectionIds || $[10] !== sections) {
    const total = getDependencyQuestionIds(sections, field).length;
    let t13;
    if ($[26] !== sections) {
      t13 = new Map(sections.flatMap(_temp2).map(_temp3));
      $[26] = sections;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    fieldsById = t13;
    let t14;
    if ($[28] !== onChange) {
      t14 = next => {
        if (!next.sectionIds.length && !next.fieldIds.length) {
          onChange(undefined);
          return;
        }
        onChange({
          ...(next.sectionIds.length ? {
            sectionIds: next.sectionIds
          } : {}),
          ...(next.fieldIds.length ? {
            fieldIds: next.fieldIds
          } : {})
        });
      };
      $[28] = onChange;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    update = t14;
    let t15;
    if ($[30] !== field || $[31] !== fieldIds || $[32] !== positions || $[33] !== sectionIds || $[34] !== sections) {
      let t16;
      if ($[36] !== field || $[37] !== sectionIds) {
        t16 = section_0 => {
          const added = sectionIds.includes(section_0.id);
          const count = getSectionDependencyPreview(section_0, field).included.length;
          return {
            display: section_0.label || "Untitled section",
            value: `${SECTION_OPTION_PREFIX}${section_0.id}`,
            groupName: "Sections",
            disabled: added,
            endLabel: added ? "Added" : questionCount(count)
          };
        };
        $[36] = field;
        $[37] = sectionIds;
        $[38] = t16;
      } else {
        t16 = $[38];
      }
      let t17;
      if ($[39] !== fieldIds || $[40] !== positions) {
        t17 = candidate_0 => {
          const added_0 = fieldIds.includes(candidate_0.id);
          return {
            display: candidate_0.label || "Untitled question",
            value: `${FIELD_OPTION_PREFIX}${candidate_0.id}`,
            groupName: "Questions",
            disabled: added_0,
            endLabel: added_0 ? "Added" : positions.get(candidate_0.id) ?? ""
          };
        };
        $[39] = fieldIds;
        $[40] = positions;
        $[41] = t17;
      } else {
        t17 = $[41];
      }
      t15 = [...sections.map(t16), ...getDependencyFieldCandidates(sections, field).map(t17)];
      $[30] = field;
      $[31] = fieldIds;
      $[32] = positions;
      $[33] = sectionIds;
      $[34] = sections;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    options = t15;
    T1 = Stack;
    t8 = 1.5;
    t9 = 1.5;
    t10 = COLORS.GRAY[50];
    t11 = `1px solid ${COLORS.GRAY[200]}`;
    t12 = 1;
    T0 = Stack;
    t3 = "row";
    t4 = "space-between";
    t5 = "center";
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Typography variant="title-xs">Uses answers from</Typography>;
      $[42] = t6;
    } else {
      t6 = $[42];
    }
    t7 = hasDependencies && <Typography variant="body-xs" sx={{
      color: COLORS.TEXT.SECONDARY
    }}>{questionCount(total)}</Typography>;
    $[4] = field;
    $[5] = fieldIds;
    $[6] = hasDependencies;
    $[7] = onChange;
    $[8] = positions;
    $[9] = sectionIds;
    $[10] = sections;
    $[11] = T0;
    $[12] = T1;
    $[13] = fieldsById;
    $[14] = options;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = update;
  } else {
    T0 = $[11];
    T1 = $[12];
    fieldsById = $[13];
    options = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
    update = $[25];
  }
  let t13;
  if ($[43] !== T0 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6 || $[48] !== t7) {
    t13 = <T0 direction={t3} justifyContent={t4} alignItems={t5}>{t6}{t7}</T0>;
    $[43] = T0;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Typography variant="body-xs" sx={{
      color: COLORS.TEXT.SECONDARY
    }}>The model reads answers from other questions as settled findings, for example a summary of every flag in a section.</Typography>;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== field || $[52] !== fieldIds || $[53] !== positions || $[54] !== sectionIds || $[55] !== sections || $[56] !== update) {
    t15 = sectionIds.map(sectionId => {
      const section_1 = sections.find(candidate_1 => candidate_1.id === sectionId);
      if (!section_1) {
        return null;
      }
      return <SectionDependencyRow key={sectionId} section={section_1} field={field} positions={positions} onRemove={() => update({
        sectionIds: sectionIds.filter(id => id !== sectionId),
        fieldIds
      })} />;
    });
    $[51] = field;
    $[52] = fieldIds;
    $[53] = positions;
    $[54] = sectionIds;
    $[55] = sections;
    $[56] = update;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== fieldIds || $[59] !== fieldsById || $[60] !== positions || $[61] !== sectionIds || $[62] !== update) {
    t16 = fieldIds.map(fieldId => {
      const dependency = fieldsById.get(fieldId);
      if (!dependency) {
        return null;
      }
      return <DependencyRowFrame key={fieldId} title={dependency.label || "Untitled question"} subtitle={dependency.dependsOn ? "Question that uses answers from other questions" : "Question"} position={positions.get(fieldId)} removeLabel={`Remove ${dependency.label || "question"}`} onRemove={() => update({
        sectionIds,
        fieldIds: fieldIds.filter(id_0 => id_0 !== fieldId)
      })} />;
    });
    $[58] = fieldIds;
    $[59] = fieldsById;
    $[60] = positions;
    $[61] = sectionIds;
    $[62] = update;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== field.id || $[65] !== fieldIds || $[66] !== isAdding || $[67] !== options || $[68] !== sectionIds || $[69] !== update) {
    t17 = isAdding && <Stack direction="row" spacing={1} alignItems="center"><Box flex={1} minWidth={0}><TypeaheadSelectInput id={`field-depends-on-search-${field.id}`} label="Section or question" hideLabel={true} placeholder="Search sections or questions" options={options} groupBy={_temp4} value="" onChange={(_event, value) => {
          if (typeof value !== "string" || !value) {
            return;
          }
          setIsAdding(false);
          if (value.startsWith(SECTION_OPTION_PREFIX)) {
            update({
              sectionIds: [...sectionIds, value.slice(SECTION_OPTION_PREFIX.length)],
              fieldIds
            });
          } else {
            update({
              sectionIds,
              fieldIds: [...fieldIds, value.slice(FIELD_OPTION_PREFIX.length)]
            });
          }
        }} /></Box><IconButton icon={XCloseIcon} size="xs" variant="transparent" ariaLabel="Cancel adding a dependency" onClick={() => setIsAdding(false)} /></Stack>;
    $[64] = field.id;
    $[65] = fieldIds;
    $[66] = isAdding;
    $[67] = options;
    $[68] = sectionIds;
    $[69] = update;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[71] !== isAdding) {
    t18 = !isAdding && <Box alignSelf="flex-start"><Button size="sm" variant="secondary" startIcon={PlusIcon} onClick={() => setIsAdding(true)}>Add section or question</Button></Box>;
    $[71] = isAdding;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  let t19;
  if ($[73] !== hasDependencies) {
    t19 = hasDependencies && <Typography variant="body-xs" sx={{
      color: COLORS.TEXT.SECONDARY
    }}>If these answers have nothing to report, this answer is blank. To give a fixed answer instead, say it in the extraction prompt.</Typography>;
    $[73] = hasDependencies;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== T1 || $[76] !== t10 || $[77] !== t11 || $[78] !== t12 || $[79] !== t13 || $[80] !== t15 || $[81] !== t16 || $[82] !== t17 || $[83] !== t18 || $[84] !== t19 || $[85] !== t8 || $[86] !== t9) {
    t20 = <T1 spacing={t8} p={t9} bgcolor={t10} border={t11} borderRadius={t12}>{t13}{t14}{t15}{t16}{t17}{t18}{t19}</T1>;
    $[75] = T1;
    $[76] = t10;
    $[77] = t11;
    $[78] = t12;
    $[79] = t13;
    $[80] = t15;
    $[81] = t16;
    $[82] = t17;
    $[83] = t18;
    $[84] = t19;
    $[85] = t8;
    $[86] = t9;
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  return t20;
}
function _temp4(option) {
  return option.groupName ?? "";
}
function _temp3(candidate) {
  return [candidate.id, candidate];
}
function _temp2(section) {
  return section.children.flatMap(_temp);
}
function _temp(child) {
  return [child, ...(child.children ?? [])];
}
interface DependencyRowFrameProps {
  title: string;
  subtitle: string;
  position?: string;
  removeLabel: string;
  onRemove: () => void;
  expanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}
function DependencyRowFrame(t0) {
  const $ = _c(25);
  const {
    title,
    subtitle,
    position,
    removeLabel,
    onRemove,
    expanded,
    onToggle,
    children
  } = t0;
  let t1;
  if ($[0] !== expanded || $[1] !== onToggle || $[2] !== title) {
    t1 = onToggle && <IconButton icon={expanded ? ChevronDownIcon : ChevronRightIcon} size="xs" variant="transparent" ariaLabel={`${expanded ? "Collapse" : "Expand"} ${title}`} onClick={onToggle} />;
    $[0] = expanded;
    $[1] = onToggle;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== title) {
    t2 = <Typography variant="title-xs" noWrap={true}>{title}</Typography>;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: COLORS.TEXT.SECONDARY
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== subtitle) {
    t4 = <Typography variant="body-xs" sx={t3}>{subtitle}</Typography>;
    $[7] = subtitle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <Stack flex={1} minWidth={0}>{t2}{t4}</Stack>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== position) {
    t6 = position && <Typography variant="eyebrow-xs" sx={{
      color: COLORS.TEXT.SECONDARY
    }}>{position}</Typography>;
    $[12] = position;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onRemove || $[15] !== removeLabel) {
    t7 = <IconButton icon={XCloseIcon} size="xs" variant="transparent" ariaLabel={removeLabel} onClick={onRemove} />;
    $[14] = onRemove;
    $[15] = removeLabel;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t1 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <Stack direction="row" spacing={1} alignItems="center" py={1} px={1}>{t1}{t5}{t6}{t7}</Stack>;
    $[17] = t1;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = expanded && children;
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <Box bgcolor="white" border={`1px solid ${COLORS.GRAY[200]}`} borderRadius={1}>{t8}{t9}</Box>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
interface SectionDependencyRowProps {
  section: FormSection;
  field: FormField;
  positions: Map<string, string>;
  onRemove: () => void;
}
function SectionDependencyRow(t0) {
  const $ = _c(44);
  const {
    section,
    field,
    positions,
    onRemove
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  let T0;
  let T1;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== expanded || $[1] !== field || $[2] !== onRemove || $[3] !== positions || $[4] !== section || $[5] !== showAll) {
    const {
      included,
      excluded
    } = getSectionDependencyPreview(section, field);
    const shown = showAll ? included : included.slice(0, SECTION_PREVIEW_LIMIT);
    const label = section.label || "Untitled section";
    T1 = DependencyRowFrame;
    t9 = label;
    t10 = `Section, ${questionCount(included.length)}`;
    t11 = `Remove ${label}`;
    t12 = onRemove;
    t13 = expanded;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => setExpanded(_temp5);
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    T0 = Stack;
    t1 = 0.75;
    t2 = `1px solid ${COLORS.GRAY[200]}`;
    t3 = 1;
    t4 = 5;
    t5 = 1.5;
    let t15;
    if ($[23] !== positions) {
      t15 = candidate => <Stack key={candidate.id} direction="row" justifyContent="space-between" spacing={1}><Typography variant="body-xs" noWrap={true}>{candidate.label || "Untitled question"}</Typography><Typography variant="eyebrow-xs" sx={{
          color: COLORS.TEXT.SECONDARY
        }}>{positions.get(candidate.id)}</Typography></Stack>;
      $[23] = positions;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    t6 = shown.map(t15);
    t7 = included.length > SECTION_PREVIEW_LIMIT && <Box alignSelf="flex-start"><Button size="xs" variant="transparent" onClick={() => setShowAll(_temp6)}>{showAll ? "Show fewer" : `Show all ${questionCount(included.length)}`}</Button></Box>;
    t8 = excluded.length > 0 && <Stack spacing={0.5} pt={0.5}><Typography variant="eyebrow-xs">Not included</Typography>{excluded.map(_temp7)}</Stack>;
    $[0] = expanded;
    $[1] = field;
    $[2] = onRemove;
    $[3] = positions;
    $[4] = section;
    $[5] = showAll;
    $[6] = T0;
    $[7] = T1;
    $[8] = t1;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    t1 = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t15;
  if ($[25] !== T0 || $[26] !== t1 || $[27] !== t2 || $[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t15 = <T0 spacing={t1} borderTop={t2} py={t3} pl={t4} pr={t5}>{t6}{t7}{t8}</T0>;
    $[25] = T0;
    $[26] = t1;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== T1 || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t9) {
    t16 = <T1 title={t9} subtitle={t10} removeLabel={t11} onRemove={t12} expanded={t13} onToggle={t14}>{t15}</T1>;
    $[35] = T1;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t9;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
function _temp7(t0) {
  const {
    field: candidate_0,
    reason
  } = t0;
  return <Typography key={candidate_0.id} variant="body-xs" sx={{
    color: COLORS.TEXT.SECONDARY
  }}>{candidate_0.label || "Untitled question"}:{" "}{EXCLUSION_REASONS[reason]}</Typography>;
}
function _temp6(current_0) {
  return !current_0;
}
function _temp5(current) {
  return !current;
}